/* Raffle Ticket — scroll-driven pin (dependency-free).
 *
 * No GSAP/ScrollTrigger: state is computed from getBoundingClientRect (viewport-
 * relative -> works in a real page AND inside the theme editor iframe). A
 * continuous rAF loop guarded by a scroll-position dirty-check drives it, so it
 * does NOT depend on scroll events firing.
 *
 * THREE positional states, ALL VISIBLE (no fade in normal flow). The RIDE LINE
 * is centerLine = vh * (rtY/100) (the per-device ticket Y). It is both the pinned
 * resting line and the trigger for BOTH handoffs, so changing rtY stays seamless:
 *   rest-start : before START-midpoint reaches the ride line -> absolute, centered
 *                on the START banner midpoint (scrolls with page; off-screen when
 *                far above, scrolls into view as you reach the Start banner).
 *   pinned     : between -> fixed at the ride line (accompanies the scroll).
 *   rest-end   : after END-midpoint passes the ride line -> absolute, centered on
 *                the END banner midpoint (parks, scrolls away).
 * At each boundary the absolute position equals the fixed position -> zero jump.
 * Honors --rt-x / --rt-y (ride line) / --rt-w per device.
 *
 * NEVER-INVISIBLE: CSS hides via `html.js` only (no flash); a delayed CSS
 * animation force-reveals at ~2.5s if this file never runs; this JS cancels it
 * and reveals once positioned. Any error -> try/catch -> static-centered visible.
 * Opacity is used ONLY for that no-flash reveal + the fallback — never mid-scroll.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var DEBUG_QS = /[?&]rtdebug=1\b/.test(window.location.search);

  function num(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }

  function initSection(section) {
    if (section.dataset.rtPinInit === '1') return;
    section.dataset.rtPinInit = '1';
    section.dataset.rtArmed = '1'; // claimed -> CSS failsafe reveal is moot

    var ticket = section.querySelector('.raffle__ticket');
    if (!ticket) return;

    ticket.style.animation = 'none'; // cancel the CSS failsafe-reveal; we control visibility now

    var DEBUG = DEBUG_QS || section.dataset.rtDebug === '1';
    var overlay = DEBUG ? ensureOverlay() : null;

    function revealStatic(reason) {
      ticket.style.position = '';
      ticket.style.top = '';
      ticket.style.opacity = '1';
      if (overlay) writeOverlay(overlay, ['STATE: static (fallback)', reason ? 'reason: ' + reason : '']);
    }

    try {
      if (REDUCED_MOTION) { revealStatic('prefers-reduced-motion'); return; }

      var banners = Array.prototype.slice.call(section.querySelectorAll('.rt-banner'));
      if (banners.length < 2) { revealStatic('fewer than 2 banners'); return; }

      // ---- Anchors: first "start", last "end"; fallbacks 2nd / last ----
      var startBanner = null, endBanner = null, i;
      for (i = 0; i < banners.length; i++) {
        if (banners[i].getAttribute('data-rt-anchor') === 'start') { startBanner = banners[i]; break; }
      }
      for (i = banners.length - 1; i >= 0; i--) {
        if (banners[i].getAttribute('data-rt-anchor') === 'end') { endBanner = banners[i]; break; }
      }
      var startFallback = !startBanner, endFallback = !endBanner;
      if (!startBanner) startBanner = banners[1];
      if (!endBanner) endBanner = banners[banners.length - 1];

      var sIdx = banners.indexOf(startBanner), eIdx = banners.indexOf(endBanner);
      if (eIdx <= sIdx) { revealStatic('END (banner#' + (eIdx + 1) + ') not after START (banner#' + (sIdx + 1) + ')'); return; }

      var rtY = num(getComputedStyle(ticket).getPropertyValue('--rt-y'), 50);
      function refreshVars() { rtY = num(getComputedStyle(ticket).getPropertyValue('--rt-y'), 50); }

      var STATE = null, ticks = 0, lastScroll = null, dirty = true, token = {};
      section._rtToken = token;

      function setState(s) {
        if (s === STATE) return;
        STATE = s;
        if (s === 'pinned') { ticket.style.position = ''; ticket.style.top = ''; }       // CSS: fixed @ --rt-y
        else { ticket.style.position = 'absolute'; }                                     // rest-start / rest-end
      }

      function midTop(banner) { return banner.offsetTop + banner.offsetHeight / 2; }     // doc coords within .raffle

      function compute() {
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var centerLine = vh * (rtY / 100); // RIDE LINE
        var startMid = startBanner.getBoundingClientRect().top + startBanner.offsetHeight / 2;
        var endMid = endBanner.getBoundingClientRect().top + endBanner.offsetHeight / 2;

        var st;
        if (startMid > centerLine) st = 'rest-start';
        else if (endMid <= centerLine) st = 'rest-end';
        else st = 'pinned';

        setState(st);
        if (st === 'rest-start') ticket.style.top = midTop(startBanner) + 'px';
        else if (st === 'rest-end') ticket.style.top = midTop(endBanner) + 'px';
        // pinned: top stays '' -> CSS top: var(--rt-y)

        ticks++;
        if (overlay) writeOverlay(overlay, [
          'STATE: ' + st,
          'ticks: ' + ticks + '   scrollY: ' + Math.round(window.scrollY || document.documentElement.scrollTop || 0),
          'vh: ' + Math.round(vh) + '   rideLine(rtY ' + rtY + '%): ' + Math.round(centerLine),
          'startMid: ' + Math.round(startMid) + '   endMid: ' + Math.round(endMid),
          'START banner#' + (sIdx + 1) + (startFallback ? ' (fallback)' : '') +
            '   END banner#' + (eIdx + 1) + (endFallback ? ' (fallback)' : '')
        ]);
      }

      function loop() {
        if (!section.isConnected || section._rtToken !== token) return; // stop if removed/re-inited
        if (!document.hidden) {
          var sy = window.scrollY || document.documentElement.scrollTop || 0;
          if (dirty || sy !== lastScroll) { lastScroll = sy; dirty = false; compute(); }
        }
        requestAnimationFrame(loop);
      }

      function markDirty() { dirty = true; }
      window.addEventListener('resize', function () { refreshVars(); markDirty(); });
      window.addEventListener('orientationchange', function () { setTimeout(function () { refreshVars(); markDirty(); }, 250); });
      window.addEventListener('load', markDirty);

      // Banners are image-driven natural height -> re-measure when images load or
      // when any height changes, so the pin midpoints are never stale.
      Array.prototype.forEach.call(section.querySelectorAll('.rt-banner__img'), function (img) {
        if (!img.complete) img.addEventListener('load', markDirty);
      });
      if (typeof ResizeObserver !== 'undefined') {
        try { new ResizeObserver(markDirty).observe(section); } catch (e) {}
      }

      compute();                 // position correctly BEFORE revealing (no position-flash)
      ticket.style.opacity = '1'; // reveal once, instant (no fade); never touched again in normal flow
      requestAnimationFrame(loop);

    } catch (e) {
      if (window.console) console.warn('[raffle] init error -> static visible', e);
      try { revealStatic('exception: ' + (e && e.message)); } catch (e2) { ticket.style.opacity = '1'; }
    }
  }

  // ---- Debug overlay ----
  function ensureOverlay() {
    var el = document.getElementById('rt-debug-overlay');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'rt-debug-overlay';
    el.className = 'rt-debug-overlay';
    (document.body || document.documentElement).appendChild(el);
    return el;
  }
  function writeOverlay(el, lines) { el.textContent = lines.filter(Boolean).join('\n'); }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.raffle'), initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target && e.target.querySelector ? e.target.querySelector('.raffle') : null;
    if (!s) return;
    s.dataset.rtPinInit = '';
    initSection(s);
  });
})();
